/**
 * Calendar-period arithmetic for forwards v2, mirroring `app/forwards2/periods.py`.
 *
 * A period is exactly one calendar month, quarter, half-year or year and is carried over the wire
 * as its label — `2026-04`, `2026-Q2`, `2026-H1`, `2026`. Periods therefore either nest or do not
 * overlap, which lets the timeline place wider listings across quarter columns without estimating.
 *
 * Dates are handled as plain `YYYY-MM-DD` strings and integer year/month arithmetic, never through
 * `Date`: a `Date` is a UTC instant and any local-timezone round trip shifts a period boundary by
 * a day.
 */

export type PeriodWidth = "month" | "quarter" | "half_year" | "year";

/** A parsed period label. The numbered field matching its width is set for sub-year labels. */
export interface ParsedPeriod {
  width: PeriodWidth;
  year: number;
  /** 1-12, month labels only. */
  month?: number;
  /** 1-4, quarter labels only. */
  quarter?: number;
  /** 1-2, half-year labels only. */
  halfYear?: number;
}

/** A year and a quarter number. */
export interface QuarterRef {
  year: number;
  quarter: number;
}

/** One column of a timeline row: the window quarter it starts at, how many columns it eats, and its cell. */
export interface TimelineSlot<T> {
  quarter: string;
  span: number;
  cell: T | null;
}

export const MONTHS_PER_QUARTER = 3;
export const MONTHS_PER_HALF_YEAR = 6;
export const MONTHS_PER_YEAR = 12;
export const QUARTERS_PER_YEAR = 4;
export const HALF_YEARS_PER_YEAR = 2;

/** `pools_timeline_service.TIMELINE_YEAR_MIN`/`MAX`: the years the pools timeline route accepts. */
export const TIMELINE_YEAR_MIN = 2000;
export const TIMELINE_YEAR_MAX = 2100;

const PERIOD_LABEL = /^(\d{4})(?:-(?:Q([1-4])|H([1-2])|(0[1-9]|1[0-2])))?$/;

const SLOT_OF_PERIOD: Record<
  PeriodWidth,
  (period: ParsedPeriod) => number | undefined
> = {
  month: (period) => period.month,
  quarter: (period) => period.quarter,
  half_year: (period) => period.halfYear,
  year: () => 1,
};

/**
 * Parses `2026`, `2026-H1`, `2026-Q2` or `2026-04`.
 *
 * @returns the parsed period, or null when the string is not a period label.
 */
export function parsePeriodLabel(label: string): ParsedPeriod | null {
  const matched = PERIOD_LABEL.exec(label.trim());
  if (!matched) return null;

  const year = Number(matched[1]);
  if (matched[2])
    return { width: "quarter", year, quarter: Number(matched[2]) };
  if (matched[3])
    return { width: "half_year", year, halfYear: Number(matched[3]) };
  if (matched[4]) return { width: "month", year, month: Number(matched[4]) };
  return { width: "year", year };
}

/** Inverse of {@link parsePeriodLabel}. */
export function formatPeriodLabel(period: ParsedPeriod): string {
  const year = String(period.year).padStart(4, "0");
  if (period.width === "quarter") return `${year}-Q${period.quarter}`;
  if (period.width === "half_year") return `${year}-H${period.halfYear}`;
  if (period.width === "month") {
    return `${year}-${String(period.month).padStart(2, "0")}`;
  }
  return year;
}

/** `2026-Q2` for the given year and quarter number. */
export function quarterLabel(quarter: QuarterRef): string {
  return formatPeriodLabel({
    width: "quarter",
    year: quarter.year,
    quarter: quarter.quarter,
  });
}

/**
 * Parses a quarter label.
 *
 * @returns the quarter, or null when the label is not a quarter label.
 */
export function parseQuarterLabel(label: string): QuarterRef | null {
  const parsed = parsePeriodLabel(label);
  if (!parsed || parsed.width !== "quarter" || parsed.quarter === undefined) {
    return null;
  }
  return { year: parsed.year, quarter: parsed.quarter };
}

/** The quarter `delta` quarters after `quarter` (negative steps backwards). */
export function shiftQuarter(quarter: QuarterRef, delta: number): QuarterRef {
  const zeroBased =
    quarter.year * QUARTERS_PER_YEAR + (quarter.quarter - 1) + delta;
  return {
    year: Math.floor(zeroBased / QUARTERS_PER_YEAR),
    quarter:
      (((zeroBased % QUARTERS_PER_YEAR) + QUARTERS_PER_YEAR) %
        QUARTERS_PER_YEAR) +
      1,
  };
}

/**
 * The quarter label `delta` quarters from `label`; an unparseable label counts from the current
 * quarter.
 */
export function shiftQuarterLabel(label: string, delta: number): string {
  return quarterLabel(shiftQuarter(quarterOrCurrent(label), delta));
}

/** The quarter containing `at` (defaults to today), as a label. */
export function currentQuarterLabel(at: Date = new Date()): string {
  return quarterLabel({
    year: at.getFullYear(),
    quarter: Math.floor(at.getMonth() / MONTHS_PER_QUARTER) + 1,
  });
}

/** The pools timeline's columns: the four quarters of one calendar year. */
export function timelineQuarters(year: number): string[] {
  return quartersOf(String(year));
}

/** The quarters a period covers: one for month/quarter, two for half-year, four for year. */
export function quartersOf(label: string): string[] {
  const parsed = parsePeriodLabel(label);
  if (!parsed) return [];

  if (parsed.width === "quarter") return [label];
  if (parsed.width === "month") {
    return [
      quarterLabel({
        year: parsed.year,
        quarter: Math.floor((parsed.month! - 1) / MONTHS_PER_QUARTER) + 1,
      }),
    ];
  }
  if (parsed.width === "half_year") {
    const firstQuarter = parsed.halfYear === 1 ? 1 : 3;
    return [firstQuarter, firstQuarter + 1].map((quarter) =>
      quarterLabel({ year: parsed.year, quarter })
    );
  }
  return Array.from({ length: QUARTERS_PER_YEAR }, (_unused, index) =>
    quarterLabel({ year: parsed.year, quarter: index + 1 })
  );
}

/** The first day of a period, as `YYYY-MM-DD`. */
export function periodStartDate(label: string): string | null {
  const parsed = parsePeriodLabel(label);
  if (!parsed) return null;
  return isoDate(parsed.year, firstMonthOf(parsed));
}

/** The exclusive end of a period — the first day of the following period, as `YYYY-MM-DD`. */
export function periodEndDate(label: string): string | null {
  const parsed = parsePeriodLabel(label);
  if (!parsed) return null;

  const zeroBased =
    parsed.year * MONTHS_PER_YEAR +
    (firstMonthOf(parsed) - 1) +
    monthsOfWidth(parsed.width);
  return isoDate(
    Math.floor(zeroBased / MONTHS_PER_YEAR),
    (zeroBased % MONTHS_PER_YEAR) + 1
  );
}

/** The last day a period covers, as `YYYY-MM-DD` — what a delivery window is compared against. */
export function periodLastDay(label: string): string | null {
  const end = periodEndDate(label);
  if (!end) return null;

  const [year, month, day] = end.split("-").map(Number);
  if (day > 1) return isoDay(year, month, day - 1);
  const previous =
    month === 1
      ? { year: year - 1, month: MONTHS_PER_YEAR }
      : { year, month: month - 1 };
  return isoDay(
    previous.year,
    previous.month,
    daysInMonth(previous.year, previous.month)
  );
}

/**
 * Lays timeline cells out over a fixed window of quarter columns.
 *
 * The backend omits cells for quarters with nothing in them and marks a wider listing with
 * `span: 2` or `span: 4` on the first quarter it covers, so the row cannot simply render its cells in order:
 * gaps have to become blank tracks and a span has to consume the columns behind it. Cells outside
 * the window are dropped, a span is clipped at the window edge, and a cell landing on a column
 * already taken by a span is skipped rather than overlapping it.
 *
 * @returns exactly `windowQuarters.length` columns' worth of slots, earliest first.
 */
export function placeCells<T extends { quarter: string; span?: number | null }>(
  windowQuarters: string[],
  cells: readonly T[]
): TimelineSlot<T>[] {
  const byColumn = new Map<number, T>();
  for (const cell of cells) {
    const column = windowQuarters.indexOf(cell.quarter);
    if (column >= 0 && !byColumn.has(column)) byColumn.set(column, cell);
  }

  const slots: TimelineSlot<T>[] = [];
  let column = 0;
  while (column < windowQuarters.length) {
    const cell = byColumn.get(column);
    const span = cell
      ? Math.min(Math.max(cell.span ?? 1, 1), windowQuarters.length - column)
      : 1;
    slots.push({ quarter: windowQuarters[column], span, cell: cell ?? null });
    column += span;
  }
  return slots;
}

/**
 * The label of the period spanning `[start, end)` — the inverse of
 * {@link periodStartDate}/{@link periodEndDate}.
 *
 * @returns the label, or null when the span is not exactly one calendar month, quarter, half-year or year.
 */
export function periodLabelOfBounds(start: string, end: string): string | null {
  const first = parseIsoMonth(start);
  const last = parseIsoMonth(end);
  if (!first || !last || first.day !== 1 || last.day !== 1) return null;

  const months =
    (last.year - first.year) * MONTHS_PER_YEAR + (last.month - first.month);
  if (months === 1)
    return formatPeriodLabel({
      width: "month",
      year: first.year,
      month: first.month,
    });
  if (months === MONTHS_PER_QUARTER && first.month % MONTHS_PER_QUARTER === 1) {
    return quarterLabel({
      year: first.year,
      quarter: Math.floor((first.month - 1) / MONTHS_PER_QUARTER) + 1,
    });
  }
  if (months === MONTHS_PER_HALF_YEAR && [1, 7].includes(first.month)) {
    return formatPeriodLabel({
      width: "half_year",
      year: first.year,
      halfYear: first.month === 1 ? 1 : 2,
    });
  }
  if (months === MONTHS_PER_YEAR && first.month === 1)
    return formatPeriodLabel({ width: "year", year: first.year });
  return null;
}

/**
 * The following period of the same width.
 *
 * @returns the label, or null when the argument is not a period label.
 */
export function nextPeriodLabel(label: string): string | null {
  const parsed = parsePeriodLabel(label);
  if (!parsed) return null;

  if (parsed.width === "year")
    return formatPeriodLabel({ width: "year", year: parsed.year + 1 });
  if (parsed.width === "half_year") {
    return formatPeriodLabel({
      width: "half_year",
      year: parsed.halfYear === 2 ? parsed.year + 1 : parsed.year,
      halfYear: parsed.halfYear === 2 ? 1 : 2,
    });
  }
  if (parsed.width === "quarter")
    return quarterLabel(
      shiftQuarter({ year: parsed.year, quarter: parsed.quarter! }, 1)
    );

  const zeroBased = parsed.year * MONTHS_PER_YEAR + parsed.month!; // already the next month, zero-based
  return formatPeriodLabel({
    width: "month",
    year: Math.floor(zeroBased / MONTHS_PER_YEAR),
    month: (zeroBased % MONTHS_PER_YEAR) + 1,
  });
}

/** The period of `width` containing `at` (defaults to today). */
export function currentPeriodLabel(
  width: PeriodWidth,
  at: Date = new Date()
): string {
  if (width === "year")
    return formatPeriodLabel({ width: "year", year: at.getFullYear() });
  if (width === "half_year") {
    return formatPeriodLabel({
      width: "half_year",
      year: at.getFullYear(),
      halfYear: at.getMonth() < MONTHS_PER_HALF_YEAR ? 1 : 2,
    });
  }
  if (width === "quarter") return currentQuarterLabel(at);
  return formatPeriodLabel({
    width: "month",
    year: at.getFullYear(),
    month: at.getMonth() + 1,
  });
}

/** Every period of `width` from the start of `firstYear` to the end of `lastYear`, in order. */
export function periodLabelsOfYears(
  width: PeriodWidth,
  firstYear: number,
  lastYear: number
): string[] {
  const labels: string[] = [];
  let label: string | null = currentPeriodLabel(
    width,
    new Date(firstYear, 0, 1)
  );
  while (
    label !== null &&
    (parsePeriodLabel(label)?.year ?? Infinity) <= lastYear
  ) {
    labels.push(label);
    label = nextPeriodLabel(label);
  }
  return labels;
}

/**
 * The quarter label containing an ISO `YYYY-MM-DD` date.
 *
 * @returns the label, or null when the string is not an ISO date.
 */
export function quarterLabelOfDate(isoDate: string): string | null {
  const matched = /^(\d{4})-(\d{2})-\d{2}/.exec(isoDate.trim());
  if (!matched) return null;

  const month = Number(matched[2]);
  if (month < 1 || month > MONTHS_PER_YEAR) return null;
  return quarterLabel({
    year: Number(matched[1]),
    quarter: Math.floor((month - 1) / MONTHS_PER_QUARTER) + 1,
  });
}

/** `2026-Q2` → `Q2 26`, the compact form the timeline's column headers and the managed deals table use. */
export function shortQuarterLabel(label: string): string {
  const parsed = parseQuarterLabel(label);
  if (!parsed) return label;
  return `Q${parsed.quarter} ${String(parsed.year).slice(-2)}`;
}

/**
 * How many calendar months a period of this width spans, mirroring `PeriodWidth.months`: every
 * other width rule derives from it.
 */
export function monthsOfWidth(width: PeriodWidth): number {
  if (width === "year") return MONTHS_PER_YEAR;
  if (width === "half_year") return MONTHS_PER_HALF_YEAR;
  if (width === "quarter") return MONTHS_PER_QUARTER;
  return 1;
}

/** The recurring-slot count of an entry width: 12 months, 4 quarters, 2 half-years, or 1 year. */
export function recurringSlotCount(entryWidth: PeriodWidth): number {
  return MONTHS_PER_YEAR / monthsOfWidth(entryWidth);
}

/**
 * The recurring slot a period fills within its year, numbered from 1 as {@link recurringSlotCount}
 * counts them.
 *
 * @returns null when the label is not a period of this width.
 */
export function recurringSlotOf(
  label: string,
  entryWidth: PeriodWidth
): number | null {
  const parsed = parsePeriodLabel(label);
  if (parsed === null || parsed.width !== entryWidth) return null;
  return SLOT_OF_PERIOD[entryWidth](parsed) ?? null;
}

function parseIsoMonth(
  value: string
): { year: number; month: number; day: number } | null {
  const matched = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!matched) return null;

  const month = Number(matched[2]);
  if (month < 1 || month > MONTHS_PER_YEAR) return null;
  return { year: Number(matched[1]), month, day: Number(matched[3]) };
}

function quarterOrCurrent(label: string): QuarterRef {
  const parsed = parseQuarterLabel(label);
  if (parsed) return parsed;

  const now = new Date();
  return {
    year: now.getFullYear(),
    quarter: Math.floor(now.getMonth() / MONTHS_PER_QUARTER) + 1,
  };
}

function firstMonthOf(period: ParsedPeriod): number {
  if (period.width === "month") return period.month!;
  if (period.width === "quarter")
    return (period.quarter! - 1) * MONTHS_PER_QUARTER + 1;
  if (period.width === "half_year")
    return (period.halfYear! - 1) * MONTHS_PER_HALF_YEAR + 1;
  return 1;
}

function isoDate(year: number, month: number): string {
  return isoDay(year, month, 1);
}

function isoDay(year: number, month: number, day: number): string {
  return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function daysInMonth(year: number, month: number): number {
  return [
    31,
    isLeapYear(year) ? 29 : 28,
    31,
    30,
    31,
    30,
    31,
    31,
    30,
    31,
    30,
    31,
  ][month - 1];
}

function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}
